import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { database } from '../../firebaseConfig';
import { ref, get } from 'firebase/database';
import './BusinessAnalysisPreview.css';

function BusinessAnalysisPreview() {
    const navigate = useNavigate();
    const { analysisId } = useParams();
    const [analysisData, setAnalysisData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [selectedAnalysisJson, setSelectedAnalysisJson] = useState(null);

    useEffect(() => {
        if (analysisId && auth.currentUser) {
            loadAnalysis();
        }
    }, [analysisId]);

    const loadAnalysis = async () => {
        try {
            const userId = auth.currentUser.uid;
            console.log(`Loading analysis ${analysisId} for preview`);
            
            const analysisRef = ref(database, `businessAnalyses/${userId}/${analysisId}`);
            const snapshot = await get(analysisRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                console.log('Analysis loaded for preview:', data);
                setAnalysisData(data);
            } else {
                setError('Analyse nicht gefunden');
            }
        } catch (err) {
            console.error('Error loading analysis:', err);
            setError('Fehler beim Laden der Analyse');
        }
        setIsLoading(false);
    };

    const getMarketSaturationLabel = (value) => {
        switch (value) {
            case 'under100': return '🟢 Weniger als 100 Konkurrenten';
            case '100-1000': return '🟡 100-1000 Konkurrenten';
            case 'over1000': return '🔴 Über 1000 Konkurrenten';
            default: return value;
        }
    };

    const getLanguageLabel = (value) => {
        const languages = {
            'german': '🇩🇪 Deutsch',
            'english': '🇺🇸 Englisch',
            'spanish': '🇪🇸 Spanisch',
            'french': '🇫🇷 Französisch',
            'italian': '🇮🇹 Italienisch'
        };
        return languages[value] || value;
    };

    const getIncomeClassLabels = (incomeClassArray) => {
        const labels = {
            'low': 'Niedriges Einkommen',
            'middle': 'Mittelschicht',
            'high': 'Hohes Einkommen',
            // Legacy support
            'lower-middle': 'Untere Mittelschicht',
            'upper-middle': 'Obere Mittelschicht'
        };
        
        // Handle old single value format
        if (typeof incomeClassArray === 'string') {
            return labels[incomeClassArray] || incomeClassArray;
        }
        
        // Handle new array format
        if (Array.isArray(incomeClassArray) && incomeClassArray.length > 0) {
            return incomeClassArray.map(value => labels[value] || value).join(', ');
        }
        
        return 'Nicht angegeben';
    };

    const getSliderDescription = (value) => {
        if (value <= 3) return 'Verbesserungsbedarf';
        if (value <= 6) return 'Durchschnittlich';
        if (value <= 8) return 'Gut';
        return 'Exzellent';
    };

    const getSourceLabel = (source) => {
        const sources = {
            // Organic
            'personal': 'Persönliches Netzwerk',
            'organic-instagram': 'Organisch Instagram',
            'organic-tiktok': 'Organisch TikTok',
            'organic-youtube': 'Organisch YouTube',
            'organic-facebook': 'Organisch Facebook',
            'organic-linkedin': 'Organisch LinkedIn',
            'seo': 'SEO / Google Search',
            'podcast': 'Podcast',
            'content-marketing': 'Content Marketing',
            'referrals': 'Empfehlungen',
            'community': 'Community',
            // Paid
            'meta-ads': 'Meta Ads (Facebook & Instagram)',
            'google-ads': 'Google Ads',
            'youtube-ads': 'YouTube Ads',
            'tiktok-ads': 'TikTok Ads',
            'linkedin-ads': 'LinkedIn Ads',
            'pinterest-ads': 'Pinterest Ads',
            'twitter-ads': 'Twitter Ads',
            'spotify-ads': 'Spotify Ads',
            // Other
            'cold-email': 'Cold Email',
            'cold-calling': 'Cold Calling',
            'live-events': 'Live Events',
            'webinars': 'Webinare',
            'partnerships': 'Partnerschaften',
            'affiliate': 'Affiliate Marketing',
            'newsletter': 'Newsletter',
            // Legacy
            'ads': 'Bezahlte Werbung'
        };
        return sources[source] || source;
    };

    const getProofSourceLabel = (source) => {
        const sources = {
            'instagram': 'Instagram',
            'google': 'Google Reviews',
            'google-visibility': 'Google Visibility',
            'facebook': 'Facebook',
            'linkedin': 'LinkedIn',
            'testimonials': 'Kundenstimmen',
            'press': 'Presse & Medien',
            'awards': 'Awards & Zertifikate',
            'community': 'Community Feedback',
            'trustpilot': 'Trustpilot',
            'youtube': 'YouTube',
            'podcast': 'Podcast Erwähnungen'
        };
        return sources[source] || source;
    };

    const getPlatformLabel = (platform) => {
        const platforms = {
            'instagram': 'Instagram',
            'facebook': 'Facebook',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'linkedin': 'LinkedIn',
            'pinterest': 'Pinterest',
            'twitter_x': 'Twitter / X',
            'podcast': 'Podcast',
            'newsletter': 'Newsletter'
        };
        return platforms[platform] || platform;
    };

    const getPlatformIcon = (platform) => {
        const icons = {
            'instagram': 'fab fa-instagram',
            'facebook': 'fab fa-facebook',
            'youtube': 'fab fa-youtube',
            'tiktok': 'fab fa-tiktok',
            'linkedin': 'fab fa-linkedin',
            'pinterest': 'fab fa-pinterest',
            'twitter_x': 'fab fa-twitter',
            'podcast': 'fas fa-podcast',
            'newsletter': 'fas fa-envelope'
        };
        return icons[platform] || 'fas fa-globe';
    };

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num?.toString() || '0';
    };

    const cleanJsonData = (data) => {
        // Erstelle eine tiefe Kopie der Daten
        const cleanedData = JSON.parse(JSON.stringify(data));
        
        // Funktion zum Entfernen von false Features
        const removeFalseFeatures = (obj) => {
            if (obj && typeof obj === 'object') {
                // Wenn es ein features-Objekt ist, filtere false Werte raus
                if ('features' in obj && typeof obj.features === 'object') {
                    const filteredFeatures = {};
                    Object.keys(obj.features).forEach(key => {
                        if (obj.features[key] === true) {
                            filteredFeatures[key] = true;
                        }
                    });
                    // Nur features Objekt behalten wenn es nicht leer ist
                    if (Object.keys(filteredFeatures).length > 0) {
                        obj.features = filteredFeatures;
                    } else {
                        delete obj.features;
                    }
                }
                
                // Rekursiv durch alle verschachtelten Objekte gehen
                Object.keys(obj).forEach(key => {
                    if (typeof obj[key] === 'object' && obj[key] !== null) {
                        if (Array.isArray(obj[key])) {
                            // Wenn es ein Array ist, jeden Eintrag durchgehen
                            obj[key] = obj[key].map(item => {
                                if (typeof item === 'object' && item !== null) {
                                    removeFalseFeatures(item);
                                }
                                return item;
                            });
                        } else {
                            removeFalseFeatures(obj[key]);
                        }
                    }
                });
            }
            return obj;
        };
        
        // Sortiere die Keys in einer logischen Reihenfolge
        const sortKeys = (obj) => {
            // Definiere die gewünschte Reihenfolge der Hauptfelder
            const keyOrder = [
                'name',
                'niche',
                'brandType',
                'geoFocus',
                'language',
                'marketSaturation',
                'foundingDate',
                'targetAudience',
                'branding',
                'leadSources',
                'products',
                'socialProof',
                'socialMedia',
                'status',
                'createdAt',
                'updatedAt',
                'shareToken',
                'publishedAt'
            ];
            
            const sorted = {};
            
            // Zuerst die Keys in der definierten Reihenfolge hinzufügen
            keyOrder.forEach(key => {
                if (key in obj) {
                    sorted[key] = obj[key];
                }
            });
            
            // Dann alle anderen Keys hinzufügen, die nicht in der Liste sind
            Object.keys(obj).forEach(key => {
                if (!(key in sorted)) {
                    sorted[key] = obj[key];
                }
            });
            
            return sorted;
        };
        
        const cleaned = removeFalseFeatures(cleanedData);
        return sortKeys(cleaned);
    };

    const handleShowJson = () => {
        const cleanedData = cleanJsonData(analysisData);
        setSelectedAnalysisJson(cleanedData);
        setShowJsonModal(true);
    };

    const handleCopyJson = () => {
        const jsonString = JSON.stringify(selectedAnalysisJson, null, 2);
        navigator.clipboard.writeText(jsonString).then(() => {
            alert('JSON in Zwischenablage kopiert! ✅');
        }).catch(err => {
            console.error('Fehler beim Kopieren:', err);
            alert('Fehler beim Kopieren in die Zwischenablage');
        });
    };

    const handleCloseJsonModal = () => {
        setShowJsonModal(false);
        setSelectedAnalysisJson(null);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#00ff88';  // Neon Green - Excellent
        if (score >= 65) return '#e91e63';  // Hot Pink - Good
        if (score >= 50) return '#ffd93d';  // Cyber Yellow - Average
        return '#ff6b9d';  // Light Pink - Needs Work
    };

    // Calculate Business Strength Score
    const calculateBusinessStrength = () => {
        if (!analysisData) return null;

        // Branding Score (25%)
        const branding = analysisData.branding || {};
        const brandingScore = ((branding.brandingQuality || 5) + 
                              (branding.messageDesignAlignment || 5) + 
                              (branding.photoQuality || 5) + 
                              (branding.videoMaterial || 5)) / 4 * 10;
        
        // Lead Sources Score (25%)
        const leadCount = analysisData.leadSources?.length || 0;
        const leadAvg = leadCount > 0 
            ? analysisData.leadSources.reduce((sum, s) => sum + (s.strength || 5), 0) / leadCount 
            : 5;
        const leadScore = Math.min((leadCount * 15) + (leadAvg * 5), 100);
        
        // Product Portfolio Score (20%)
        const productCount = analysisData.products?.filter(p => p.name && p.name.trim().length > 0).length || 0;
        const hasHighTicket = analysisData.products?.some(p => ['500-1000', '1000-5000', '5000-10000', '10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
        const hasUltraHighTicket = analysisData.products?.some(p => ['10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
        const hasRecurring = analysisData.products?.some(p => p.paymentType === 'monthly') || false;
        const hasRevolutionary = analysisData.products?.some(p => p.exclusivity === 'revolutionary') || false;
        const productScore = Math.min((productCount * 15) + (hasHighTicket ? 25 : 0) + (hasUltraHighTicket ? 15 : 0) + (hasRecurring ? 30 : 0) + (hasRevolutionary ? 15 : 0), 100);
        
        // Social Proof Score (20%)
        const proofCount = analysisData.socialProof?.length || 0;
        const proofAvg = proofCount > 0 
            ? analysisData.socialProof.reduce((sum, p) => sum + (p.strength || 5), 0) / proofCount 
            : 5;
        const proofScore = Math.min((proofCount * 12) + (proofAvg * 6), 100);
        
        // Overall Score
        const overallScore = Math.round(
            (brandingScore * 0.30) +
            (leadScore * 0.30) +
            (productScore * 0.20) +
            (proofScore * 0.20)
        );
        
        // Rating
        let rating = '';
        if (overallScore >= 80) rating = 'Exzellent';
        else if (overallScore >= 65) rating = 'Gut';
        else if (overallScore >= 50) rating = 'Durchschnittlich';
        else if (overallScore >= 35) rating = 'Ausbaufähig';
        else rating = '🔴 Kritisch';

        // Strengths
        const strengths = [];
        if (brandingScore >= 75) strengths.push('⭐ Starke Markenidentität');
        if (leadCount >= 5) strengths.push('🎯 Diverse Lead-Kanäle');
        if (productScore >= 75) strengths.push('💼 Solides Produkt-Portfolio');
        if (proofScore >= 75) strengths.push('🏆 Exzellenter Social Proof');

        // Recommendations - Intelligente & umfassende Analyse
        const recommendations = [];
        
        // === CRITICAL PRIORITY (Existenzbedrohend) ===
        
        // Lead Sources - Kritisch
        if (leadCount === 0) {
            recommendations.push({
                priority: 'high',
                category: '🚨 Akute Gefahr',
                message: 'KRITISCH: Keine Lead-Quellen definiert! Ihr Business hat aktuell keinen Kundenakquise-Kanal. Sofortiger Handlungsbedarf - starten Sie mit organischen Social Media oder Ihrem persönlichen Netzwerk.'
            });
        } else if (leadCount === 1) {
            recommendations.push({
                priority: 'high',
                category: 'Lead-Generierung',
                message: 'Hohes Risiko: Nur EINE Lead-Quelle! Ein Ausfall (z.B. Algorithmus-Änderung) gefährdet Ihr gesamtes Business. Fügen Sie mindestens 2-3 weitere Kanäle hinzu.'
            });
        }
        
        // Revenue Model - Kritisch
        if (!hasRecurring && productCount > 0) {
            recommendations.push({
                priority: 'high',
                category: 'Geschäftsmodell',
                message: 'Kein wiederkehrendes Einkommen! Jeden Monat bei Null starten = instabiles Business. Erwägen Sie: Mitgliedschaften, Retainer, monatliche Services oder Produkt-Updates.'
            });
        }
        
        // Social Proof - Kritisch niedrig
        if (proofScore < 40) {
            recommendations.push({
                priority: 'high',
                category: 'Vertrauensaufbau',
                message: 'Kritischer Mangel an Social Proof! Potenzielle Kunden brauchen Beweise. Aktionsplan: 1) Sammeln Sie 10-20 Testimonials, 2) Google Reviews aufbauen, 3) Case Studies erstellen.'
            });
        }
        
        // Branding - Kritisch schwach
        if (brandingScore < 50) {
            recommendations.push({
                priority: 'high',
                category: 'Markenidentität',
                message: 'Schwache Markenpräsenz gefährdet Verkäufe. Investieren Sie in: professionelle Fotografie, einheitliches Design, klare Markenbotschaft. Erste Eindruck = oft der einzige Eindruck.'
            });
        }
        
        // Produkt-Gap
        if (productCount === 0) {
            recommendations.push({
                priority: 'high',
                category: 'Produktportfolio',
                message: 'Keine Produkte/Services definiert! Ohne Angebot keine Monetarisierung. Definieren Sie mindestens 1-2 Kernangebote mit klaren Preisen.'
            });
        }
        
        // === HIGH PRIORITY (Wichtig für Wachstum) ===
        
        // Lead Diversification
        if (leadCount >= 2 && leadCount <= 3) {
            recommendations.push({
                priority: 'high',
                category: 'Kanal-Diversifikation',
                message: 'Nur ' + leadCount + ' Lead-Quellen - noch vulnerabel. Ziel: 5-7 Kanäle. Empfehlung: Fügen Sie einen bezahlten Kanal (Ads) + einen organischen (SEO/Content) hinzu für Balance.'
            });
        }
        
        // Pricing Strategy
        if (!hasHighTicket && productCount > 0) {
            recommendations.push({
                priority: 'high',
                category: 'Premium-Strategie',
                message: 'Kein High-Ticket Angebot (€500+)! Sie lassen Umsatz liegen. Entwickeln Sie ein Premium-Paket - oft braucht es nur 5-10 High-Ticket Sales für signifikanten Impact.'
            });
        }
        
        // Ultra-Premium Opportunity
        if (hasHighTicket && !hasUltraHighTicket && brandingScore >= 70) {
            recommendations.push({
                priority: 'high',
                category: 'Skalierung',
                message: 'Ihr Branding ist stark genug für Ultra-Premium (€10k+)! VIP-Programme, 1:1 Intensiv-Betreuung oder Mastermind können mit weniger Kunden mehr Umsatz generieren.'
            });
        }
        
        // Social Proof - Mittelschwach
        if (proofScore >= 40 && proofScore < 60) {
            recommendations.push({
                priority: 'high',
                category: 'Autorität',
                message: 'Social Proof ist Basis vorhanden, aber nicht stark genug. Strategische Schritte: 1) Erreichen Sie 50+ Google Reviews, 2) Video-Testimonials sammeln, 3) Presse/Podcast-Auftritte anstreben.'
            });
        }
        
        // === MEDIUM PRIORITY (Optimierung) ===
        
        // Branding Enhancement
        if (brandingScore >= 60 && brandingScore < 75) {
            recommendations.push({
                priority: 'medium',
                category: 'Brand-Optimierung',
                message: 'Branding ist solide (Score: ' + Math.round(brandingScore) + '/100), aber Premium-Potenzial! Investieren Sie in: professionelles Video-Material, Brand Photography Session, Design-Konsistenz über alle Plattformen.'
            });
        }
        
        // Product Ladder
        if (productCount === 1) {
            recommendations.push({
                priority: 'medium',
                category: 'Produkt-Ladder',
                message: 'Nur 1 Produkt = verpasste Chancen! Bauen Sie eine Produktleiter: Entry (€50-200), Core (€500-2k), Premium (€5k+). Verschiedene Budgets = mehr Kunden erreicht.'
            });
        } else if (productCount === 2) {
            recommendations.push({
                priority: 'medium',
                category: 'Portfolio-Expansion',
                message: '2 Produkte ist gut, aber 3-4 ist ideal für vollständige Customer Journey: Einstiegsprodukt → Hauptangebot → Premium Upgrade → VIP Intensiv.'
            });
        }
        
        // Lead Source Optimization
        if (leadCount >= 4 && leadCount <= 6) {
            const organicSources = analysisData.leadSources?.filter(s => 
                ['personal', 'organic-instagram', 'organic-tiktok', 'organic-youtube', 
                 'organic-facebook', 'organic-linkedin', 'seo', 'referrals'].includes(s.source)
            ).length || 0;
            const paidSources = analysisData.leadSources?.filter(s => 
                ['meta-ads', 'google-ads', 'youtube-ads', 'tiktok-ads', 'linkedin-ads'].includes(s.source)
            ).length || 0;
            
            if (organicSources > 0 && paidSources === 0) {
                recommendations.push({
                    priority: 'medium',
                    category: 'Paid-Strategie',
                    message: 'Alle ' + leadCount + ' Quellen sind organisch - gut! Aber: Paid Ads = vorhersehbares Wachstum. Testen Sie mit €500-1000/Monat Meta oder Google Ads für skalierbaren Traffic.'
                });
            } else if (paidSources > organicSources && organicSources < 2) {
                recommendations.push({
                    priority: 'medium',
                    category: 'Organische Basis',
                    message: 'Starker Paid-Fokus, aber organische Präsenz fehlt. Risiko: Werbung stoppen = Traffic stoppt. Bauen Sie SEO/Content-Marketing für nachhaltige Basis auf.'
                });
            }
        }
        
        // === LOW PRIORITY (Nice-to-Have & Positive Feedback) ===
        
        // Positive Reinforcement
        if (hasRecurring) {
            recommendations.push({
                priority: 'low',
                category: 'Geschäftsmodell',
                message: '✅ Wiederkehrendes Einkommen vorhanden! Das ist die Basis für skalierbares Business. Optimierung: Erhöhen Sie den Anteil von Recurring Revenue auf 60-80% des Gesamt-Umsatzes.'
            });
        }
        
        if (hasRevolutionary) {
            recommendations.push({
                priority: 'low',
                category: 'Wettbewerbsvorteil',
                message: '🚀 Revolutionäres Produkt identifiziert! Das ist Gold wert. Marketing-Tipp: Positionieren Sie sich als Marktführer & Innovator. Nutzen Sie "First-Mover" Narrative in Ihrer Kommunikation.'
            });
        }
        
        if (hasUltraHighTicket) {
            recommendations.push({
                priority: 'low',
                category: 'Premium-Positionierung',
                message: '👑 Ultra-Premium Angebot (€10k+) vorhanden! Sie spielen in der Top-Liga. Fokus: VIP-Experience, exklusive Community, außergewöhnlicher Service. Weniger Kunden, mehr Wert pro Kunde.'
            });
        }
        
        if (leadCount >= 7) {
            recommendations.push({
                priority: 'low',
                category: 'Lead-Exzellenz',
                message: '✅ Exzellente Kanal-Diversifikation (' + leadCount + ' Quellen)! Ihr Business ist resilient gegen Marktveränderungen. Optimierung: Tracken Sie Cost-per-Lead pro Kanal für ROI-Maximierung.'
            });
        }
        
        if (brandingScore >= 80) {
            recommendations.push({
                priority: 'low',
                category: 'Brand-Stärke',
                message: '⭐ Premium-Branding (Score: ' + Math.round(brandingScore) + '/100)! Ihre visuelle Identität ist erstklassig. Nutzen Sie dies: höhere Preise rechtfertigen, Brand-Partnerschaften suchen, Lizenzierung erwägen.'
            });
        }
        
        if (proofScore >= 75) {
            recommendations.push({
                priority: 'low',
                category: 'Autorität',
                message: '🏆 Starker Social Proof! Sie haben Vertrauen aufgebaut. Nächster Schritt: Werden Sie zur Thought Leader - Gastbeiträge, Keynote-Speaking, Branchen-Awards anstreben.'
            });
        }
        
        // Advanced Kombinationsempfehlungen
        if (hasHighTicket && proofScore >= 70 && brandingScore >= 70) {
            recommendations.push({
                priority: 'low',
                category: 'Scale-Ready',
                message: '🎯 Perfekte Kombination: High-Ticket + starkes Branding + Social Proof. Sie sind bereit zu skalieren! Nächste Schritte: Sales Team aufbauen, Paid Ads hochfahren, Partnerschaften eingehen.'
            });
        }
        
        if (leadCount >= 5 && hasRecurring && productCount >= 3) {
            recommendations.push({
                priority: 'low',
                category: 'Business Health',
                message: '💪 Solides Business-Fundament! Diverse Leads + Recurring Revenue + Produktportfolio = nachhaltiges Wachstum. Fokus jetzt: Optimierung & Skalierung statt neue Grundlagen.'
            });
        }

        // Calculate total followers from lead sources
        const totalFollowers = analysisData.leadSources?.reduce((sum, source) => {
            const count = parseInt(source.followerCount) || 0;
            return sum + count;
        }, 0) || 0;

        const socialProofCount = analysisData.socialProof?.length || 0;

        return {
            scores: {
                overall: overallScore,
                branding: Math.round(brandingScore),
                leadSources: Math.round(leadScore),
                products: Math.round(productScore),
                socialProof: Math.round(proofScore)
            },
            rating,
            strengths,
            recommendations: recommendations.sort((a, b) => {
                const priorityOrder = { 'high': 0, 'medium': 1, 'low': 2 };
                return priorityOrder[a.priority] - priorityOrder[b.priority];
            }),
            metrics: {
                leadCount,
                productCount,
                hasRecurring,
                hasHighTicket,
                hasUltraHighTicket,
                totalFollowers,
                socialProofCount
            }
        };
    };

    if (isLoading) {
        return (
            <div className="business-analyse-preview-loading">
                <div className="business-analyse-preview-spinner"></div>
                <p>Lade Analyse...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="business-analyse-preview-error">
                <div className="business-analyse-preview-error-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <h2>{error}</h2>
                    <button 
                        className="business-analyse-preview-back-btn"
                        onClick={() => navigate('/business-analysis')}
                    >
                        Zurück zur Übersicht
                    </button>
                </div>
            </div>
        );
    }

    if (!analysisData) {
        return null;
    }

    const businessAnalysis = calculateBusinessStrength();

    return (
        <div className="business-analyse-preview">
            {/* Header */}
            <div className="business-analyse-preview-header">
                <div className="business-analyse-preview-header-content">
                    <div className="business-analyse-preview-title-section">
                        <button 
                            className="business-analyse-preview-back-btn"
                            onClick={() => navigate('/business-analysis')}
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <div>
                            <h1 className="business-analyse-preview-title">{analysisData.name}</h1>
                            <div className="business-analyse-preview-meta">
                                <span className="business-analyse-preview-brand-type">
                                    <i className="fas fa-building"></i>
                                    {analysisData.brandType === 'personal' ? 'Personal Brand' : 'Corporate Brand'}
                                </span>
                                <span className="business-analyse-preview-date">
                                    <i className="fas fa-calendar"></i>
                                    Erstellt am {new Date(analysisData.createdAt).toLocaleDateString('de-DE')}
                                </span>
                                {analysisData.updatedAt !== analysisData.createdAt && (
                                    <span className="business-analyse-preview-date">
                                        <i className="fas fa-edit"></i>
                                        Zuletzt bearbeitet am {new Date(analysisData.updatedAt).toLocaleDateString('de-DE')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="business-analyse-preview-actions">
                        <button 
                            className="business-analyse-preview-info-btn"
                            onClick={handleShowJson}
                            title="JSON Daten anzeigen"
                        >
                            <i className="fas fa-code"></i>
                            JSON
                        </button>
                        <button 
                            className="business-analyse-preview-edit-btn"
                            onClick={() => navigate(`/business-analysis/edit/${analysisId}`)}
                        >
                            <i className="fas fa-edit"></i>
                            Bearbeiten
                        </button>
                    </div>
                </div>
            </div>

            <div className="business-analyse-preview-container">
                {/* Business Score Overview */}
                {businessAnalysis && (
                    <>
                        {/* Overall Business Score Card */}
                        <div className="business-analyse-overview-hero">
                            <div className="business-analyse-overview-score-section">
                                <div className="business-analyse-overview-score-circle">
                                    <svg width="180" height="180" viewBox="0 0 180 180">
                                        <circle
                                            cx="90"
                                            cy="90"
                                            r="80"
                                            fill="none"
                                            stroke="rgba(226, 232, 240, 0.3)"
                                            strokeWidth="12"
                                        />
                                        <circle
                                            cx="90"
                                            cy="90"
                                            r="80"
                                            fill="none"
                                            stroke={getScoreColor(businessAnalysis.scores.overall)}
                                            strokeWidth="12"
                                            strokeDasharray={`${(businessAnalysis.scores.overall / 100) * 502.4} 502.4`}
                                            strokeLinecap="round"
                                            transform="rotate(-90 90 90)"
                                        />
                                    </svg>
                                    <div className="business-analyse-overview-score-content">
                                        <span className="business-analyse-overview-score-number">{businessAnalysis.scores.overall}</span>
                                        <span className="business-analyse-overview-score-label">Score</span>
                                    </div>
                                </div>
                                <div className="business-analyse-overview-score-info">
                                    <h2 className="business-analyse-overview-rating">{businessAnalysis.rating}</h2>
                                    <p className="business-analyse-overview-description">
                                        {businessAnalysis.scores.overall >= 80 ? 'Hervorragende Performance in allen Bereichen!' :
                                         businessAnalysis.scores.overall >= 65 ? 'Solide Basis mit Optimierungspotential.' :
                                         businessAnalysis.scores.overall >= 50 ? 'Ausbaufähig - Fokus auf kritische Bereiche legen.' :
                                         businessAnalysis.scores.overall >= 35 ? 'Dringender Handlungsbedarf in mehreren Bereichen.' :
                                         'Fundamentale Probleme - Sofortmaßnahmen erforderlich.'}
                                    </p>
                                </div>
                            </div>

                            {/* Quick Metrics */}
                            <div className="business-analyse-overview-quick-metrics">
                                <div className="business-analyse-quick-metric">
                                    <i className="fas fa-funnel-dollar"></i>
                                    <div>
                                        <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.leadCount}</span>
                                        <span className="business-analyse-quick-metric-label">Lead-Quellen</span>
                                    </div>
                                </div>
                                <div className="business-analyse-quick-metric">
                                    <i className="fas fa-box"></i>
                                    <div>
                                        <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.productCount}</span>
                                        <span className="business-analyse-quick-metric-label">Produkte</span>
                                    </div>
                                </div>
                                <div className="business-analyse-quick-metric">
                                    <i className="fas fa-users"></i>
                                    <div>
                                        <span className="business-analyse-quick-metric-value">{formatNumber(businessAnalysis.metrics.totalFollowers)}</span>
                                        <span className="business-analyse-quick-metric-label">Gesamt Follower</span>
                                    </div>
                                </div>
                                <div className="business-analyse-quick-metric">
                                    <i className="fas fa-certificate"></i>
                                    <div>
                                        <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.socialProofCount}</span>
                                        <span className="business-analyse-quick-metric-label">Social Proof</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Category Scores Breakdown */}
                        <div className="business-analyse-preview-section">
                            <div className="business-analyse-preview-section-header">
                                <h2>
                                    <i className="fas fa-chart-bar"></i>
                                    Detaillierte Score-Übersicht
                                </h2>
                            </div>
                            <div className="business-analyse-preview-content">
                                <div className="business-analyse-category-scores">
                                    <div className="business-analyse-category-score-item">
                                        <div className="business-analyse-category-score-header">
                                            <div>
                                                <i className="fas fa-palette"></i>
                                                <span>Branding & Markenimage</span>
                                            </div>
                                            <strong>{businessAnalysis.scores.branding}/100</strong>
                                        </div>
                                        <div className="business-analyse-category-score-bar">
                                            <div 
                                                className="business-analyse-category-score-fill"
                                                style={{ 
                                                    width: `${businessAnalysis.scores.branding}%`,
                                                    background: getScoreColor(businessAnalysis.scores.branding)
                                                }}
                                            ></div>
                                        </div>
                                        <p className="business-analyse-category-score-note">Gewichtung: 25% des Gesamtscores</p>
                                    </div>

                                    <div className="business-analyse-category-score-item">
                                        <div className="business-analyse-category-score-header">
                                            <div>
                                                <i className="fas fa-funnel-dollar"></i>
                                                <span>Lead-Quellen-Stärke</span>
                                            </div>
                                            <strong>{businessAnalysis.scores.leadSources}/100</strong>
                                        </div>
                                        <div className="business-analyse-category-score-bar">
                                            <div 
                                                className="business-analyse-category-score-fill"
                                                style={{ 
                                                    width: `${businessAnalysis.scores.leadSources}%`,
                                                    background: getScoreColor(businessAnalysis.scores.leadSources)
                                                }}
                                            ></div>
                                        </div>
                                        <p className="business-analyse-category-score-note">Gewichtung: 25% des Gesamtscores</p>
                                    </div>

                                    <div className="business-analyse-category-score-item">
                                        <div className="business-analyse-category-score-header">
                                            <div>
                                                <i className="fas fa-box"></i>
                                                <span>Produkt-Portfolio</span>
                                            </div>
                                            <strong>{businessAnalysis.scores.products}/100</strong>
                                        </div>
                                        <div className="business-analyse-category-score-bar">
                                            <div 
                                                className="business-analyse-category-score-fill"
                                                style={{ 
                                                    width: `${businessAnalysis.scores.products}%`,
                                                    background: getScoreColor(businessAnalysis.scores.products)
                                                }}
                                            ></div>
                                        </div>
                                        <p className="business-analyse-category-score-note">Gewichtung: 20% des Gesamtscores</p>
                                    </div>

                                    <div className="business-analyse-category-score-item">
                                        <div className="business-analyse-category-score-header">
                                            <div>
                                                <i className="fas fa-certificate"></i>
                                                <span>Social Proof & Vertrauen</span>
                                            </div>
                                            <strong>{businessAnalysis.scores.socialProof}/100</strong>
                                        </div>
                                        <div className="business-analyse-category-score-bar">
                                            <div 
                                                className="business-analyse-category-score-fill"
                                                style={{ 
                                                    width: `${businessAnalysis.scores.socialProof}%`,
                                                    background: getScoreColor(businessAnalysis.scores.socialProof)
                                                }}
                                            ></div>
                                        </div>
                                        <p className="business-analyse-category-score-note">Gewichtung: 15% des Gesamtscores</p>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Strengths */}
                        {businessAnalysis.strengths.length > 0 && (
                            <div className="business-analyse-preview-section business-analyse-strengths-section">
                                <div className="business-analyse-preview-section-header">
                                    <h2>
                                        <i className="fas fa-star"></i>
                                        Ihre Stärken ({businessAnalysis.strengths.length})
                                    </h2>
                                </div>
                                <div className="business-analyse-preview-content">
                                    <div className="business-analyse-strengths-grid">
                                        {businessAnalysis.strengths.map((strength, index) => (
                                            <div key={index} className="business-analyse-strength-card">
                                                <i className="fas fa-check-circle"></i>
                                                <span>{strength}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Grunddaten Section */}
                <div className="business-analyse-preview-section">
                    <div className="business-analyse-preview-section-header">
                        <h2>
                            <i className="fas fa-info-circle"></i>
                            Grunddaten
                        </h2>
                    </div>
                    <div className="business-analyse-preview-content">
                        <div className="business-analyse-preview-grid">
                            <div className="business-analyse-preview-field">
                                <label>Nische</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.niche || 'Nicht angegeben'}
                                </div>
                            </div>
                            <div className="business-analyse-preview-field">
                                <label>Geografischer Fokus</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.geoFocus || 'Nicht angegeben'}
                                </div>
                            </div>
                            <div className="business-analyse-preview-field">
                                <label>Marktsättigung</label>
                                <div className="business-analyse-preview-value">
                                    {getMarketSaturationLabel(analysisData.marketSaturation)}
                                </div>
                            </div>
                            {analysisData.foundingDate && (
                                <div className="business-analyse-preview-field">
                                    <label>Gründung / Aktivitätsstart</label>
                                    <div className="business-analyse-preview-value">
                                        {new Date(analysisData.foundingDate).toLocaleDateString('de-DE')}
                                    </div>
                                </div>
                            )}
                        </div>
                        {analysisData.targetAudience && (typeof analysisData.targetAudience === 'object' ? (
                            <div className="business-analyse-preview-target-audience">
                                <h3 className="business-analyse-preview-subsection-title">
                                    <i className="fas fa-users"></i>
                                    Zielgruppe
                                </h3>
                                
                                {analysisData.targetAudience.description && (
                                    <div className="business-analyse-preview-field-full">
                                        <label>Allgemeine Beschreibung</label>
                                        <div className="business-analyse-preview-value">
                                            {analysisData.targetAudience.description}
                                        </div>
                                    </div>
                                )}

                                <div className="business-analyse-preview-demographics">
                                    <div className="business-analyse-preview-demo-item">
                                        <i className="fas fa-venus-mars"></i>
                                        <div>
                                            <span className="business-analyse-preview-demo-label">Geschlecht</span>
                                            <span className="business-analyse-preview-demo-value">
                                                {analysisData.targetAudience.gender === 'all' ? 'Alle Geschlechter' :
                                                 analysisData.targetAudience.gender === 'male' ? 'Männlich' :
                                                 analysisData.targetAudience.gender === 'female' ? 'Weiblich' : 
                                                 analysisData.targetAudience.gender}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="business-analyse-preview-demo-item">
                                        <i className="fas fa-euro-sign"></i>
                                        <div>
                                            <span className="business-analyse-preview-demo-label">Einkommen</span>
                                            <span className="business-analyse-preview-demo-value">
                                                {getIncomeClassLabels(analysisData.targetAudience.incomeClass)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {analysisData.targetAudience.painPoints && (
                                    <div className="business-analyse-preview-field-full pain-points">
                                        <label>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Pain Points
                                        </label>
                                        <div className="business-analyse-preview-value">
                                            {analysisData.targetAudience.painPoints}
                                        </div>
                                    </div>
                                )}

                                {analysisData.targetAudience.goals && (
                                    <div className="business-analyse-preview-field-full goals">
                                        <label>
                                            <i className="fas fa-bullseye"></i>
                                            Ziele & Wünsche
                                        </label>
                                        <div className="business-analyse-preview-value">
                                            {analysisData.targetAudience.goals}
                                        </div>
                                    </div>
                                )}

                                {analysisData.targetAudience.antiCustomer && (
                                    <div className="business-analyse-preview-field-full" style={{ background: 'rgba(255, 107, 157, 0.05)', border: '1px solid rgba(255, 107, 157, 0.15)' }}>
                                        <label style={{ color: 'var(--business-analyse-world-danger)' }}>
                                            <i className="fas fa-user-times" style={{ color: 'var(--business-analyse-world-danger)' }}></i>
                                            Anti-Kunde
                                        </label>
                                        <div className="business-analyse-preview-value">
                                            {analysisData.targetAudience.antiCustomer}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="business-analyse-preview-field-full">
                                <label>Zielgruppe</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.targetAudience}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Branding Section */}
                {analysisData.branding && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-palette"></i>
                                Markenimage & Branding
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            {analysisData.branding.embodiment && (
                                <div className="business-analyse-preview-field-full">
                                    <label>Was verkörpert Ihre Marke?</label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.embodiment}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.emotionalTriggers && (
                                <div className="business-analyse-preview-field-full">
                                    <label>Emotionale Trigger</label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.emotionalTriggers}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.design && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-palette"></i>
                                        Design & Farbpsychologie
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.design}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.wording && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-comment-dots"></i>
                                        Wording & Tonalität
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.wording}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.values && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-gem"></i>
                                        Kernwerte
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.values}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.hiddenPsychology && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-user-secret"></i>
                                        Versteckte Psychologie
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.hiddenPsychology}
                                    </div>
                                </div>
                            )}
                            
                            <div className="business-analyse-preview-ratings">
                                <div className="business-analyse-preview-rating">
                                    <label>Branding-Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.brandingQuality / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.brandingQuality}/10 - {getSliderDescription(analysisData.branding.brandingQuality)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Message-Design Übereinstimmung</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.messageDesignAlignment / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.messageDesignAlignment}/10 - {getSliderDescription(analysisData.branding.messageDesignAlignment)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Foto-Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.photoQuality / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.photoQuality}/10 - {getSliderDescription(analysisData.branding.photoQuality)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Video-Material Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.videoMaterial / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.videoMaterial}/10 - {getSliderDescription(analysisData.branding.videoMaterial)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="business-analyse-preview-field">
                                <label>Zielgeschlecht</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.branding.targetGender === 'all' ? 'Alle Geschlechter' :
                                     analysisData.branding.targetGender === 'male' ? 'Hauptsächlich Männlich' :
                                     analysisData.branding.targetGender === 'female' ? 'Hauptsächlich Weiblich' :
                                     analysisData.branding.targetGender}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lead Sources Section */}
                {analysisData.leadSources && analysisData.leadSources.length > 0 && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-funnel-dollar"></i>
                                Lead-Quellen ({analysisData.leadSources.length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-lead-sources">
                                {analysisData.leadSources.map((source, index) => (
                                    <div key={source.id || index} className="business-analyse-preview-lead-source">
                                        <div className="business-analyse-preview-lead-source-header">
                                            <h4>{getSourceLabel(source.source)}</h4>
                                            <div className="business-analyse-preview-strength">
                                                <span>Stärke: {source.strength}/10</span>
                                                <div className="business-analyse-preview-strength-bar">
                                                    <div 
                                                        className="business-analyse-preview-strength-fill"
                                                        style={{ width: `${(source.strength / 10) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                        {source.notes && (
                                            <div className="business-analyse-preview-lead-notes">
                                                <i className="fas fa-sticky-note"></i>
                                                {source.notes}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Products Section */}
                {analysisData.products && analysisData.products.length > 0 && analysisData.products.some(p => p.name?.trim()) && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-box"></i>
                                Produkte ({analysisData.products.filter(p => p.name?.trim()).length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-products">
                                {analysisData.products.filter(p => p.name?.trim()).map((product, index) => (
                                    <div key={product.id || index} className="business-analyse-preview-product">
                                        <h4>{product.name}</h4>
                                        <div className="business-analyse-preview-product-details">
                                            <span className="business-analyse-preview-product-type">
                                                {product.type === 'physical' ? 'Physisches Produkt' :
                                                 product.type === 'digital' ? 'Digitales Produkt' :
                                                 product.type === 'mix' ? 'Mix aus beiden' : product.type}
                                            </span>
                                            <span className="business-analyse-preview-product-payment">
                                                {product.paymentType === 'monthly' ? 'Monatlich' : 'Einmalzahlung'}
                                            </span>
                                        </div>
                                        {product.additionalInfo && (
                                            <div className="business-analyse-preview-product-info">
                                                {product.additionalInfo}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Social Proof Section */}
                {analysisData.socialProof && analysisData.socialProof.length > 0 && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-certificate"></i>
                                Social Proof & Vertrauen ({analysisData.socialProof.length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-lead-sources">
                                {analysisData.socialProof.map((proof, index) => (
                                    <div key={proof.id || index} className="business-analyse-preview-lead-source">
                                        <div className="business-analyse-preview-lead-source-header">
                                            <h4>{getProofSourceLabel(proof.source)}</h4>
                                            <div className="business-analyse-preview-strength">
                                                <span>Vertrauensstärke: {proof.strength}/10</span>
                                                <div className="business-analyse-preview-strength-bar">
                                                    <div 
                                                        className="business-analyse-preview-strength-fill"
                                                        style={{ width: `${(proof.strength / 10) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                        {proof.notes && (
                                            <div className="business-analyse-preview-lead-notes">
                                                <i className="fas fa-sticky-note"></i>
                                                {proof.notes}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Recommendations - Am Ende */}
                {businessAnalysis && businessAnalysis.recommendations.length > 0 && (
                    <div className="business-analyse-preview-section business-analyse-recommendations-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-lightbulb"></i>
                                Handlungsempfehlungen ({businessAnalysis.recommendations.length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-recommendations-list">
                                {businessAnalysis.recommendations.map((rec, index) => (
                                    <div 
                                        key={index} 
                                        className={`business-analyse-recommendation-card priority-${rec.priority}`}
                                    >
                                        <div className="business-analyse-recommendation-header">
                                            <span className={`business-analyse-priority-badge ${rec.priority}`}>
                                                {rec.priority === 'high' ? '🔴 Hohe Priorität' :
                                                 rec.priority === 'medium' ? '🟡 Mittlere Priorität' :
                                                 '🟢 Niedrige Priorität'}
                                            </span>
                                            <span className="business-analyse-recommendation-category">
                                                {rec.category}
                                            </span>
                                        </div>
                                        <p className="business-analyse-recommendation-message">{rec.message}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Empty State für fehlende Daten */}
                {(!analysisData.branding?.embodiment && !analysisData.leadSources?.length && !analysisData.products?.some(p => p.name?.trim()) && !analysisData.socialProof?.length) && (
                    <div className="business-analyse-preview-empty">
                        <div className="business-analyse-preview-empty-content">
                            <i className="fas fa-clipboard-list"></i>
                            <h3>Analyse noch nicht vollständig</h3>
                            <p>Fügen Sie weitere Informationen hinzu, um eine umfassende Business-Analyse zu erstellen.</p>
                            <button 
                                className="business-analyse-preview-edit-btn"
                                onClick={() => navigate(`/business-analysis/edit/${analysisId}`)}
                            >
                                <i className="fas fa-edit"></i>
                                Jetzt bearbeiten
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* JSON Modal */}
            {showJsonModal && selectedAnalysisJson && (
                <div className="business-analyse-modal-overlay" onClick={handleCloseJsonModal}>
                    <div className="business-analyse-json-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="business-analyse-modal-header">
                            <h2>
                                <i className="fas fa-code"></i>
                                Analyse Daten (JSON)
                            </h2>
                            <button 
                                className="business-analyse-modal-close"
                                onClick={handleCloseJsonModal}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="business-analyse-json-content">
                            <div className="business-analyse-json-header">
                                <div className="business-analyse-json-info">
                                    <span className="business-analyse-json-badge">
                                        <i className="fas fa-database"></i>
                                        {selectedAnalysisJson.name || 'Unbenannte Analyse'}
                                    </span>
                                    <span className="business-analyse-json-size">
                                        {JSON.stringify(selectedAnalysisJson).length} Zeichen
                                    </span>
                                </div>
                                <button 
                                    className="business-analyse-copy-btn"
                                    onClick={handleCopyJson}
                                >
                                    <i className="fas fa-copy"></i>
                                    Kopieren
                                </button>
                            </div>
                            
                            <pre className="business-analyse-json-display">
                                <code>{JSON.stringify(selectedAnalysisJson, null, 2)}</code>
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BusinessAnalysisPreview;
